import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, EMPTY, merge, Observable, of, timer } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { isEqual, keyBy } from 'lodash-es';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IAuthenticationService } from '@nimbus-library/features/authentication/services/i-authentication.service';
import { DispatcherType } from '@nimbus-library/features/shared/models/enum/dispatcher-type.enum';
import { PickupDisableReason } from '@nimbus-library/features/shared/models/enum/pickup-disable-reason.enum';
import { CombinedConversationState } from '@nimbus-library/features/shared/models/enum/conversation-status.enum';
import { DEFAULT_AUDIO_FILE } from '@nimbus-library/features/shared/services/media/predefined-audio.service';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { PickupSignalPreferencesModel } from '@nimbus-library/features/user-settings/models/pickup-signal-preferences.model';
import { PickupSignalPreferencesStore } from '@nimbus-library/features/user-settings/services/pickup-signal-preferences.store';
import { BaseUserPreferencesNotificator } from '@nimbus-library/features/user-settings/services/user-preferences-notification-sender/user-preferences-notificators/base/base-user-preferences-notificator.service';
import { UserPreferencesNotificationAudioService } from '@nimbus-library/features/user-settings/services/user-preferences-notification-sender/user-preferences-notification-audio.service';
import { TeamInfoService } from '@nimbus-library/features/shared/services/team/team-info.service';
import { TeamWidget } from '@nimbus-library/features/shared/services/team/model/team-widget.model';
import { ReportingServiceSessionsService } from '@nimbus-global/features/reportingLiveData/reporting-service-sessions.service';
import { TeamConversationRecord } from '@nimbus-global/features/reporting/models/conversations/team-conversation-record.model';
import { FeatureFlippingService } from '@nimbus-global/features/feature-flipping/feature-flipping.service';
import { Feature } from '@nimbus-global/features/feature-flipping/feature.enum';
import { NimbusSignalRHub } from '@nimbus-global/features/signalr/nimbusSignalR.hub';

const PICKUP_DISPATCHER_TYPES = [DispatcherType.Pickup, DispatcherType.PickupConference];
// Safety re-poll of team-widgets while a pickup is queued, matching the AC membership poll cadence.
// The SignalR membership event drives the prompt stop/start; this just covers any missed event.
const ACTIVE_STATE_POLL_MS = 5000;

/**
 * Notifies (single chime per new pickup + browser notification) when a pick-up call the agent can
 * actually act on is waiting in queue, on any page. Mirrors the Attendant Console task card's visibility: a
 * pickup must be dispatcher Pickup/PickupConference, explicitly pickable (pickupDisableReason None),
 * not a skill-based team the user is not in the pool for, and — for teams-based teams — the agent
 * must be ACTIVE in that team. Active/inactive state is sourced reactively from
 * {@link TeamInfoService.getTeamWidgets} (member.enabled), refreshed on the SignalR membership
 * event and a fallback poll, so the signal stops as soon as the card disappears. Gated behind the
 * PickupSignal feature flag; preferences come from the separate {@link PickupSignalPreferencesStore}.
 */
@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class UserPreferencesNotificatorByPickupService extends BaseUserPreferencesNotificator {
  private signalledConversationIds = new Set<string>();

  protected audioType: string = 'PickupSignal';

  public constructor(
    protected readonly userPreferencesNotificationAudioService: UserPreferencesNotificationAudioService,
    private readonly reportingServiceSessions: ReportingServiceSessionsService,
    private readonly translateService: TranslateService,
    private readonly router: Router,
    private readonly preferencesStore: PickupSignalPreferencesStore,
    private readonly featureFlippingService: FeatureFlippingService,
    private readonly authenticationService: IAuthenticationService,
    private readonly teamInfoService: TeamInfoService,
    private readonly signalRHub: NimbusSignalRHub,
  ) {
    super(userPreferencesNotificationAudioService);
  }

  public initialize(): void {
    // Auth state is established asynchronously inside Teams (SSO token exchange populates
    // user info only after `aquireIdToken$` resolves). Reading `getUserInfo()` eagerly at
    // app bootstrap returns undefined there and would permanently disable the signal, so gate
    // on `isAuthenticated$` first — matching how the rest of the app defers auth-dependent work.
    this.authenticationService.isAuthenticated$
      .pipe(
        filter(Boolean),
        take(1),
        switchMap(() => {
          const userInfo = this.authenticationService.getUserInfo();
          if (!userInfo) {
            return EMPTY;
          }
          const { userId, tenantId } = userInfo;

          return this.featureFlippingService.isEnabledInFeatureManagementByTenantId$(tenantId, Feature.PickupSignal).pipe(
            take(1),
            catchError(() => of(false)),
            filter((enabled) => enabled),
            switchMap(() => this.preferencesStore.load(userId, tenantId)),
            switchMap(() =>
              combineLatest([
                this.preferencesStore.preferences$.pipe(distinctUntilChanged(isEqual)),
                this.visiblePickups$(userId, tenantId),
              ]),
            ),
            tap(([preferences, pickups]) => this.handlePickups(preferences, pickups)),
          );
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  /**
   * Pickups that an enabled pickup card would currently show for this agent. Re-evaluates the
   * agent's active/team state on every membership-change SignalR event (and on (re)connect), with
   * a fallback poll while a pickup is queued.
   */
  private visiblePickups$(userId: string, tenantId: string): Observable<TeamConversationRecord[]> {
    const pickups$ = this.reportingServiceSessions
      .getServiceSessions$([], [CombinedConversationState.InQueue])
      .pipe(map((sessions) => sessions.filter((session) => this.isPickable(session))));

    const reEvaluate$ = merge(
      this.signalRHub.isConnected$.pipe(filter((connected) => !!connected)),
      this.signalRHub.onUserTaskStateChanged$.pipe(filter((event) => event.userId === userId && !!event.changedTeamMembership)),
    );

    return combineLatest([pickups$, reEvaluate$]).pipe(
      switchMap(([pickups]) =>
        pickups.length === 0
          ? of([] as TeamConversationRecord[])
          : timer(0, ACTIVE_STATE_POLL_MS).pipe(
              switchMap(() => this.teamInfoService.getTeamWidgets(tenantId, userId).pipe(catchError(() => of([] as TeamWidget[])))),
              map((widgets) => this.filterToVisibleEnabled(pickups, widgets)),
            ),
      ),
      distinctUntilChanged((a, b) => isEqual(this.conversationIds(a), this.conversationIds(b))),
    );
  }

  private handlePickups(preferences: PickupSignalPreferencesModel, pickups: TeamConversationRecord[]): void {
    const currentIds = new Set(pickups.map((p) => p.conversationId));
    for (const id of this.signalledConversationIds) {
      if (!currentIds.has(id)) {
        this.signalledConversationIds.delete(id);
        this.removeIdNotified(id);
      }
    }

    const newPickups = pickups.filter((p) => !this.signalledConversationIds.has(p.conversationId));
    if (newPickups.length === 0) {
      return;
    }
    for (const pickup of newPickups) {
      this.signalledConversationIds.add(pickup.conversationId);
    }

    if (preferences.audioEnabled) {
      this.playAudioOnce(preferences.audioStyle || DEFAULT_AUDIO_FILE);
    }

    if (preferences.desktopNotificationEnabled) {
      const title = this.translateService.instant(TranslationKeys.AttendantConsolePickupSignalSectionTitle);
      newPickups.forEach((pickup) =>
        this.showDesktopNotification(pickup.conversationId, title, pickup.serviceContactInfo?.serviceName || title, (): void => {
          this.router.navigateByUrl('personal/attendantconsole').then();
        }),
      );
    }
  }

  /** A pickup the card would render an enabled button for, ignoring team active state (cheap, session-only). */
  private isPickable(session: TeamConversationRecord): boolean {
    return PICKUP_DISPATCHER_TYPES.includes(session.dispatcherType) && session.pickupDisableReason === PickupDisableReason.None;
  }

  /**
   * Drops pickups the card would hide or disable, mirroring ac-task-card `isPickUpDisabled` +
   * `AcTasksState.queueTasksByActiveTeams` + my-queue-widget: suppress skill-based teams the user
   * is not in the pool for (no reason), and teams-based teams where the agent is inactive
   * (member not enabled). Unknown team ⇒ keep, matching the card's `!team ⇒ visible`.
   */
  private filterToVisibleEnabled(pickups: TeamConversationRecord[], widgets: TeamWidget[]): TeamConversationRecord[] {
    const widgetByTeamId = keyBy(widgets, (widget) => widget.teamId);
    return pickups.filter((pickup) => {
      const widget = widgetByTeamId[pickup.teamId];
      if (!widget) {
        return true;
      }
      if (widget.skillBasedTeam && !pickup.pickupDisableReason) {
        return false;
      }
      return !(widget.hasMembers && widget.member?.info && !widget.member.info.enabled);
    });
  }

  private conversationIds(pickups: TeamConversationRecord[]): string[] {
    return pickups.map((pickup) => pickup.conversationId).sort();
  }
}
